import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { enqueueSnackbar } from 'notistack';
import { signOut } from 'processes/auth/helpers/signOut';
import { Config } from 'shared/config/_common';
import { getAccessToken } from 'shared/helpers/getAccessToken';
import { endImpersonation, isImpersonationMode } from 'shared/helpers/impersonation';
import { authStore } from 'processes/auth/model/state';
import { AuthResponse } from 'entities/Auth/api/types';
import i18next from 'i18next';

export const axiosInstance = axios.create({
	baseURL: Config.API_URL,
	headers: {
		'Content-Type': 'application/json',
	},
	withCredentials: true,
});

interface FailedRequest {
	resolve: (value: AxiosResponse<unknown> | PromiseLike<AxiosResponse<unknown>>) => void;
	reject: (reason?: unknown) => void;
}

const NETWORK_ERROR_MAX_RETRIES = 2;
const NETWORK_ERROR_RETRY_DELAY = 5000;

let isRefreshing = false;
let failedQueue: FailedRequest[] = [];

const processQueue = (error: AxiosError | null, response?: AxiosResponse<unknown>) => {
	failedQueue.forEach((prom) => {
		if (error) {
			prom.reject(error);
		} else if (response) {
			prom.resolve(response);
		}
	});
	failedQueue = [];
};

axiosInstance.interceptors.request.use(
	function (config) {
		const token = getAccessToken();
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	},
	function (error) {
		return Promise.reject(error);
	},
);

axiosInstance.interceptors.response.use(
	function (response) {
		return response;
	},
	async function (error: AxiosError) {
		const originalRequest = error.config as InternalAxiosRequestConfig & {
			_retry?: boolean;
			_networkRetryCount?: number;
		};

		if (error.code === AxiosError.ERR_NETWORK && originalRequest) {
			const retryCount = originalRequest._networkRetryCount ?? 0;

			if (retryCount < NETWORK_ERROR_MAX_RETRIES) {
				originalRequest._networkRetryCount = retryCount + 1;

				await new Promise<void>((resolve, reject) => {
					const timeoutId = setTimeout(resolve, NETWORK_ERROR_RETRY_DELAY);
					originalRequest.signal?.addEventListener?.('abort', () => {
						clearTimeout(timeoutId);
						reject(error);
					});
				});

				return axiosInstance(originalRequest);
			}
		}

		if (error.response?.status === 401 && !originalRequest._retry) {
			if (isImpersonationMode()) {
				endImpersonation();
				return Promise.reject(error);
			}

			if (isRefreshing) {
				return new Promise<AxiosResponse>((resolve, reject) => {
					failedQueue.push({ resolve, reject });
				})
					.then(() => axiosInstance(originalRequest))
					.catch((err) => Promise.reject(err));
			}

			originalRequest._retry = true;
			isRefreshing = true;

			try {
				const response = await axios.post<Partial<AuthResponse> & { accessToken: string }>(
					`${Config.API_URL}/auth/refresh`,
					{},
					{
						withCredentials: true,
						headers: { 'Content-Type': 'application/json' },
					},
				);

				const { accessToken, ...rest } = response.data;
				authStore.getState().updateAuthData({ accessToken, ...rest });

				processQueue(null, response);

				originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
				return axiosInstance(originalRequest);
			} catch (refreshError) {
				processQueue(refreshError as AxiosError);
				signOut();

				return Promise.reject(refreshError);
			} finally {
				isRefreshing = false;
			}
		}

		if (error.response?.status === 412) {
			authStore.getState().setPasswordExpired(true);
			return Promise.reject(error);
		}

		if (error.response?.status === 429) {
			const retryAfter = parseInt((error.response.headers['retry-after'] as string) || '60');
			enqueueSnackbar({
				message: i18next.t('server_errors.TOO_MANY_REQUESTS', { retryAfter }),
				variant: 'warning',
			});
		} else if (error.response) {
			// eslint-disable-next-line no-console
			console.error('Request made and server responded with a non-2xx status code:\n', error);
		} else if (error.request) {
			// eslint-disable-next-line no-console
			console.error('Request made but no response received:\n', error);
		} else {
			// eslint-disable-next-line no-console
			console.error('An error occurred while setting up the request:\n', error);
		}

		return Promise.reject(error);
	},
);
